import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  Plus,
  RotateCcw,
  Building2,
  Loader2,
  Eye,
  Edit2,
  Save,
} from "lucide-react";
import { PaginationControls } from "@/components/common/PaginationControl";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { config } from "@/config";
import { SettingsApi } from "@/api/giftcard.api";

interface Mall {
  mallId: number;
  mallName: string;
  phoneNumber: string;
  address: string;
  createdDate: string;
  updatedDate: string;
  createdByName?: string;
  updatedByName?: string;
}

const mallValidationSchema = Yup.object({
  mallName: Yup.string()
    .required("Mall name is required")
    .min(2, "Mall name must be at least 2 characters")
    .max(100, "Mall name cannot exceed 100 characters"),
  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(/^[0-9]+$/, "Phone number must contain only digits")
    .min(7, "Phone number must be at least 7 digits")
    .max(15, "Phone number must not exceed 15 digits"),
  address: Yup.string()
    .required("Address is required")
    .min(5, "Address must be at least 5 characters")
    .max(500, "Address cannot exceed 500 characters"),
});

const GiftCardMall: React.FC = () => {
  const [mallList, setMallList] = useState<Mall[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddMallModalOpen, setIsAddMallModalOpen] = useState(false);
  const [isCreatingMall, setIsCreatingMall] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedMall, setSelectedMall] = useState<Mall | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editFormData, setEditFormData] = useState<{
    mallName: string;
    phoneNumber: string;
    address: string;
  }>({
    mallName: "",
    phoneNumber: "",
    address: "",
  });

  // Fetch mall list
  const fetchMallList = async () => {
    setIsLoading(true);
    try {
      const data = await SettingsApi.getMallDetails();
      const mallsData = Array.isArray(data?.malldetails)
        ? data.malldetails
        : [];
      const mappedMalls: Mall[] = mallsData.map((mall: any) => ({
        mallId: mall.mallId || 0,
        mallName: mall.mallName || "N/A",
        phoneNumber: mall.phoneNumber || mall.phoneNo || "N/A",
        address: mall.address || "N/A",
        createdDate: mall.createdDate || "",
        updatedDate: mall.updatedDate || "",
        createdByName: mall.createdByName || "N/A",
        updatedByName: mall.updatedByName || "N/A",
      }));
      setMallList(mappedMalls);
    } catch (error) {
      console.error("Error fetching mall list:", error);
      toast.error("Failed to fetch mall list");
      setMallList([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Create new mall
  const createMall = async (mallData: {
    mallName: string;
    phoneNumber: string;
    address: string;
  }) => {
    setIsCreatingMall(true);
    try {
      const userInfo = sessionStorage.getItem("userInfo");
      const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
      const userName = parsedUserInfo?.username || "";

      const response = await fetch(
        `${config.GIFT_API_BASE_URL}/Settings/CreateUpdateMall`,
        {
          method: "POST",
          headers: {
            xapikey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            p_MallId: 0, // 0 for new mall
            p_MallName: mallData.mallName,
            p_PhoneNo: mallData.phoneNumber,
            p_Address: mallData.address,
            p_UserName: userName,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create mall");
      }

      const data = await response.json();

      if (data.error_msg) {
        toast.error(data.error_msg || "Failed to create mall");
        return;
      }

      toast.success("Mall created successfully!");
      setIsAddMallModalOpen(false);
      // Refresh mall list
      await fetchMallList();
    } catch (error) {
      console.error("Error creating mall:", error);
      toast.error("Failed to create mall. Please try again.");
    } finally {
      setIsCreatingMall(false);
    }
  };

  // Update mall
  const updateMall = async (mallData: {
    mallName: string;
    phoneNumber: string;
    address: string;
  }) => {
    if (!selectedMall) return;

    setIsSaving(true);
    try {
      const userInfo = sessionStorage.getItem("userInfo");
      const parsedUserInfo = userInfo ? JSON.parse(userInfo) : null;
      const userName = parsedUserInfo?.username || "";

      const response = await fetch(
        `${config.GIFT_API_BASE_URL}/Settings/CreateUpdateMall`,
        {
          method: "POST",
          headers: {
            xapikey: config.GIFT_API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            p_MallId: selectedMall.mallId, // Current mall ID
            p_MallName: mallData.mallName,
            p_PhoneNo: mallData.phoneNumber,
            p_Address: mallData.address,
            p_UserName: userName,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update mall");
      }

      const data = await response.json();

      if (data.error_msg) {
        toast.error(data.error_msg || "Failed to update mall");
        return;
      }

      toast.success("Mall updated successfully!");
      setIsEditMode(false);
      setIsViewModalOpen(false);
      // Refresh mall list
      await fetchMallList();
    } catch (error) {
      console.error("Error updating mall:", error);
      toast.error("Failed to update mall. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle view mall
  const handleViewMall = (mall: Mall) => {
    setSelectedMall(mall);
    setEditFormData({
      mallName: mall.mallName,
      phoneNumber: mall.phoneNumber,
      address: mall.address,
    });
    setIsEditMode(false);
    setIsViewModalOpen(true);
  };

  // Handle edit click
  const handleEditClick = () => {
    if (selectedMall) {
      setEditFormData({
        mallName: selectedMall.mallName,
        phoneNumber: selectedMall.phoneNumber,
        address: selectedMall.address,
      });
      setIsEditMode(true);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (selectedMall) {
      setEditFormData({
        mallName: selectedMall.mallName,
        phoneNumber: selectedMall.phoneNumber,
        address: selectedMall.address,
      });
    }
    setIsEditMode(false);
  };

  useEffect(() => {
    fetchMallList();
  }, []);

  // Filter malls
  const filteredMalls = mallList.filter(
    (mall) =>
      mall.mallName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mall.phoneNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mall.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredMalls.length / rowsPerPage);
  const currentPageData = filteredMalls.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  // Reset filters
  const handleReset = () => {
    setSearchTerm("");
    setPage(0);
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    if (!dateString || dateString === "N/A" || dateString === "") return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "N/A";
    }
  };

  // Table columns
  const columns: any[] = [
    {
      header: "Mall Id",
      accessorKey: "mallId",
      cell: (row: Mall) => (
        <div className="font-medium text-gray-900">{row.mallId}</div>
      ),
    },
    {
      header: "Created Date",
      accessorKey: "createdDate",
      cell: (row: Mall) => (
        <span className="text-sm text-gray-700">
          {formatDate(row.createdDate)}
        </span>
      ),
    },
    {
      header: "Updated Date",
      accessorKey: "updatedDate",
      cell: (row: Mall) => (
        <span className="text-sm text-gray-700">
          {formatDate(row.updatedDate)}
        </span>
      ),
    },
    {
      header: "Mall Name",
      accessorKey: "mallName",
      cell: (row: Mall) => (
        <div className="font-medium text-gray-900">{row.mallName}</div>
      ),
    },
    {
      header: "Phone Number",
      accessorKey: "phoneNumber",
    },
    {
      header: "Address",
      accessorKey: "address",
      cell: (row: Mall) => (
        <span
          className="text-sm text-gray-600 line-clamp-1"
          title={row.address}
        >
          {row.address}
        </span>
      ),
    },
    {
      header: "Created By",
      accessorKey: "createdByName",
      cell: (row: Mall) => (
        <span className="text-sm text-gray-700">
          {row.createdByName || "N/A"}
        </span>
      ),
    },
    {
      header: "Updated By",
      accessorKey: "updatedByName",
      cell: (row: Mall) => (
        <span className="text-sm text-gray-700">
          {row.updatedByName || "N/A"}
        </span>
      ),
    },
    {
      header: "Action",
      id: "action",
      cell: (row: Mall) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleViewMall(row)}
          className="gap-2"
        >
          <Eye className="h-4 w-4" />
          View
        </Button>
      ),
    },
  ];

  const initialValues = {
    mallName: "",
    phoneNumber: "",
    address: "",
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-8 w-8 text-amber-600" />
            Mall
          </h1>
          <p className="text-gray-600 mt-1">
            Manage and view all malls in the gift card system
          </p>
        </div>
        <Button
          onClick={() => setIsAddMallModalOpen(true)}
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Mall
        </Button>
      </div>

      {/* Filter Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by Mall Name, Phone Number, or Address"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Reset Button */}
            <Button variant="outline" onClick={handleReset} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
              <span className="ml-2 text-gray-600">Loading malls...</span>
            </div>
          ) : (
            <DataTable title="Malls" data={currentPageData} columns={columns} />
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!isLoading && (
        <div className="mt-4 flex justify-between items-center">
          {/* Rows per page selector */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-700">Rows per page:</span>
            <Select
              value={rowsPerPage.toString()}
              onValueChange={(value) => {
                setRowsPerPage(parseInt(value));
                setPage(0); // Reset to first page when changing rows per page
              }}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="30">30</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pagination controls */}
          {totalPages > 1 && (
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              onPrevHandler={() => setPage(Math.max(0, page - 1))}
              onNextHandler={() => setPage(Math.min(totalPages - 1, page + 1))}
              onPageChange={setPage}
            />
          )}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredMalls.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No malls found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {mallList.length === 0
              ? "No malls available. Add your first mall."
              : "Try adjusting your search criteria"}
          </p>
        </div>
      )}

      {/* Add New Mall Modal */}
      <Dialog open={isAddMallModalOpen} onOpenChange={setIsAddMallModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-amber-700">
              Add New Mall
            </DialogTitle>
            <DialogDescription>
              Enter the details to create a new mall
            </DialogDescription>
          </DialogHeader>

          <Formik
            initialValues={initialValues}
            validationSchema={mallValidationSchema}
            onSubmit={async (values, { resetForm }) => {
              await createMall(values);
              resetForm();
            }}
          >
            {({ isSubmitting }) => (
              <Form className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="mallName">Mall Name *</Label>
                  <Field name="mallName">
                    {({ field, meta }: any) => (
                      <div>
                        <Input
                          {...field}
                          id="mallName"
                          type="text"
                          placeholder="Enter mall name"
                          className={
                            meta.touched && meta.error ? "border-red-500" : ""
                          }
                        />
                        <ErrorMessage
                          name="mallName"
                          component="p"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                    )}
                  </Field>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number *</Label>
                  <Field name="phoneNumber">
                    {({ field, meta }: any) => (
                      <div>
                        <Input
                          {...field}
                          id="phoneNumber"
                          type="tel"
                          placeholder="Enter phone number"
                          className={
                            meta.touched && meta.error ? "border-red-500" : ""
                          }
                        />
                        <ErrorMessage
                          name="phoneNumber"
                          component="p"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                    )}
                  </Field>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address *</Label>
                  <Field name="address">
                    {({ field, meta }: any) => (
                      <div>
                        <Textarea
                          {...field}
                          id="address"
                          placeholder="Enter address"
                          rows={3}
                          className={
                            meta.touched && meta.error ? "border-red-500" : ""
                          }
                        />
                        <ErrorMessage
                          name="address"
                          component="p"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                    )}
                  </Field>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddMallModalOpen(false)}
                    disabled={isSubmitting || isCreatingMall}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || isCreatingMall}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {isCreatingMall ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Mall"
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* View/Edit Mall Modal */}
      <Dialog
        open={isViewModalOpen}
        onOpenChange={(open) => {
          setIsViewModalOpen(open);
          if (!open) {
            setIsEditMode(false);
            setSelectedMall(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl font-bold text-amber-700">
                  {isEditMode ? "Edit Mall" : "Mall Details"}
                </DialogTitle>
                <DialogDescription>
                  {isEditMode
                    ? "Update mall information"
                    : "View complete information about the mall"}
                </DialogDescription>
              </div>
              {!isEditMode && selectedMall && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEditClick}
                  className="gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </Button>
              )}
            </div>
          </DialogHeader>

          {selectedMall && (
            <div className="space-y-6 mt-4">
              {isEditMode ? (
                <Formik
                  initialValues={editFormData}
                  validationSchema={mallValidationSchema}
                  onSubmit={async (values) => {
                    await updateMall(values);
                  }}
                  enableReinitialize
                >
                  {({ isSubmitting }) => (
                    <Form className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="editMallName">Mall Name *</Label>
                        <Field name="mallName">
                          {({ field, meta }: any) => (
                            <div>
                              <Input
                                {...field}
                                id="editMallName"
                                type="text"
                                placeholder="Enter mall name"
                                className={
                                  meta.touched && meta.error
                                    ? "border-red-500"
                                    : ""
                                }
                              />
                              <ErrorMessage
                                name="mallName"
                                component="p"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>
                          )}
                        </Field>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="editPhoneNumber">Phone Number *</Label>
                        <Field name="phoneNumber">
                          {({ field, meta }: any) => (
                            <div>
                              <Input
                                {...field}
                                id="editPhoneNumber"
                                type="tel"
                                placeholder="Enter phone number"
                                className={
                                  meta.touched && meta.error
                                    ? "border-red-500"
                                    : ""
                                }
                              />
                              <ErrorMessage
                                name="phoneNumber"
                                component="p"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>
                          )}
                        </Field>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="editAddress">Address *</Label>
                        <Field name="address">
                          {({ field, meta }: any) => (
                            <div>
                              <Textarea
                                {...field}
                                id="editAddress"
                                placeholder="Enter address"
                                rows={3}
                                className={
                                  meta.touched && meta.error
                                    ? "border-red-500"
                                    : ""
                                }
                              />
                              <ErrorMessage
                                name="address"
                                component="p"
                                className="text-red-500 text-sm mt-1"
                              />
                            </div>
                          )}
                        </Field>
                      </div>

                      <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleCancelEdit}
                          disabled={isSubmitting || isSaving}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={isSubmitting || isSaving}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">
                        Mall ID
                      </Label>
                      <p className="text-base text-gray-900">
                        {selectedMall.mallId}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">
                        Mall Name
                      </Label>
                      <p className="text-base text-gray-900">
                        {selectedMall.mallName}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">
                        Phone Number
                      </Label>
                      <p className="text-base text-gray-900">
                        {selectedMall.phoneNumber}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">
                        Created Date
                      </Label>
                      <p className="text-base text-gray-900">
                        {formatDate(selectedMall.createdDate)}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-gray-700">
                        Updated Date
                      </Label>
                      <p className="text-base text-gray-900">
                        {formatDate(selectedMall.updatedDate)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-gray-700">
                      Address
                    </Label>
                    <p className="text-base text-gray-900">
                      {selectedMall.address}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiftCardMall;
